import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  QrCode, Package, CheckCircle, ArrowLeft, ArrowRight,
  Camera, CameraOff, Loader2, AlertTriangle, Wifi,
  Thermometer, Battery, MapPin, ScanBarcode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Html5Qrcode } from 'html5-qrcode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = ['device', 'pallet', 'confirm', 'success'];

const BulkActivation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [manualInput, setManualInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const html5QrCodeRef = useRef(null);

  // Data collected during the workflow
  const [deviceData, setDeviceData] = useState(null);
  const [palletId, setPalletId] = useState('');
  const [activationResult, setActivationResult] = useState(null);

  // Optional shipment details
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');

  const stopScanner = async () => {
    try {
      if (html5QrCodeRef.current?.isScanning) {
        await html5QrCodeRef.current.stop();
      }
    } catch {
      // Scanner may already be stopped
    }
    setIsScanning(false);
  };

  const startScanner = async () => {
    try {
      setCameraError(null);
      setIsScanning(true);
      html5QrCodeRef.current = new Html5Qrcode('qr-reader');
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => handleScanResult(decodedText),
        () => {}
      );
    } catch (err) {
      setCameraError(err.message || 'Unable to access camera');
      setIsScanning(false);
    }
  };

  useEffect(() => {
    return () => { stopScanner(); };
  }, []);

  const handleScanResult = async (code) => {
    await stopScanner();
    if (currentStep === 0) {
      await lookupDevice(code);
    } else if (currentStep === 1) {
      setPalletId(code);
      setCurrentStep(2);
      toast.success('Pallet scanned');
    }
  };

  const lookupDevice = async (code) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/bulk-activate/lookup-device`, {
        params: { code },
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.has_active_shipment) {
        toast.error(`Device already linked to shipment ${res.data.active_shipment_sscc}`);
        setIsLoading(false);
        return;
      }
      setDeviceData(res.data.device);
      setCurrentStep(1);
      toast.success(`Device found: ${res.data.device.name || res.data.device.device_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Device not found');
    }
    setIsLoading(false);
  };

  const handleManualSubmit = () => {
    const val = manualInput.trim();
    if (!val) { toast.error('Enter a code'); return; }
    setManualInput('');
    handleScanResult(val);
  };

  const handleActivate = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/bulk-activate`, {
        device_id: deviceData.device_id,
        pallet_id: palletId,
        origin,
        destination,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setActivationResult(res.data);
      setCurrentStep(3);
      toast.success('Shipment activated!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed');
    }
    setIsLoading(false);
  };

  const resetWorkflow = () => {
    setCurrentStep(0);
    setDeviceData(null);
    setPalletId('');
    setActivationResult(null);
    setOrigin('');
    setDestination('');
    setManualInput('');
  };

  // Scan UI shared between step 0 (device) and step 1 (pallet)
  const ScanUI = ({ label, hint }) => (
    <div className="space-y-4" data-testid="scan-ui">
      <div className="text-center">
        <h2 className="text-xl font-bold text-slate-900">{label}</h2>
        <p className="text-sm text-slate-500 mt-1">{hint}</p>
      </div>

      <div className="relative mx-auto w-full max-w-[300px] aspect-square bg-slate-900 rounded-2xl overflow-hidden">
        <div id="qr-reader" className="w-full h-full" />
        {!isScanning && !cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <Camera className="w-14 h-14 text-white/40 mb-3" />
            <p className="text-white/60 text-sm">Tap Start Camera</p>
          </div>
        )}
        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
            <CameraOff className="w-10 h-10 text-red-400 mb-2" />
            <p className="text-white text-sm text-center">{cameraError}</p>
          </div>
        )}
        {isScanning && (
          <div className="absolute inset-4 border-2 border-cyan-400/50 rounded-xl pointer-events-none">
            <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-cyan-400 rounded-tl-lg" />
            <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-cyan-400 rounded-tr-lg" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-cyan-400 rounded-bl-lg" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-cyan-400 rounded-br-lg" />
          </div>
        )}
      </div>

      <div className="flex gap-2">
        {!isScanning ? (
          <Button onClick={startScanner} className="flex-1 h-12 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500" data-testid="start-camera-btn">
            <Camera className="w-5 h-5 mr-2" /> Start Camera
          </Button>
        ) : (
          <Button onClick={stopScanner} variant="outline" className="flex-1 h-12 rounded-xl" data-testid="stop-camera-btn">
            <CameraOff className="w-5 h-5 mr-2" /> Stop
          </Button>
        )}
      </div>

      <div className="border-t pt-4">
        <p className="text-xs text-slate-500 mb-2 text-center">Or enter manually</p>
        <div className="flex gap-2">
          <Input
            placeholder={currentStep === 0 ? 'Device IMEI or ID' : 'Pallet barcode'}
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
            className="h-11 rounded-xl"
            data-testid="manual-code-input"
          />
          <Button onClick={handleManualSubmit} className="h-11 px-5 rounded-xl" data-testid="manual-submit-btn">
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Go'}
          </Button>
        </div>
      </div>
    </div>
  );

  // Confirmation step
  const ConfirmUI = () => (
    <div className="space-y-5" data-testid="confirm-step">
      <div className="text-center">
        <h2 className="text-xl font-bold text-slate-900">Confirm Activation</h2>
        <p className="text-sm text-slate-500 mt-1">Review details and activate</p>
      </div>

      {/* Device info */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-bold text-blue-700">
            <Wifi className="w-4 h-4" /> Device
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div><span className="text-slate-500">Name:</span> <span className="font-semibold">{deviceData?.name}</span></div>
            <div><span className="text-slate-500">ID:</span> <span className="font-mono font-semibold">{deviceData?.device_id}</span></div>
            {deviceData?.battery_level != null && (
              <div className="flex items-center gap-1"><Battery className="w-3 h-3 text-slate-400" /> {deviceData.battery_level}%</div>
            )}
            {deviceData?.last_temperature != null && (
              <div className="flex items-center gap-1"><Thermometer className="w-3 h-3 text-slate-400" /> {deviceData.last_temperature}°C</div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pallet info */}
      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm font-bold text-green-700">
            <Package className="w-4 h-4" /> Pallet
          </div>
          <div className="text-sm">
            <span className="text-slate-500">ID:</span> <span className="font-mono font-semibold">{palletId}</span>
          </div>
        </CardContent>
      </Card>

      {/* Optional fields */}
      <div className="space-y-3">
        <div className="space-y-1">
          <Label className="text-sm">Origin (optional)</Label>
          <Input value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="e.g. Oslo Warehouse" className="h-11 rounded-xl" data-testid="origin-input" />
        </div>
        <div className="space-y-1">
          <Label className="text-sm">Destination (optional)</Label>
          <Input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="e.g. Bergen Store" className="h-11 rounded-xl" data-testid="destination-input" />
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="outline" onClick={() => { setPalletId(''); setCurrentStep(1); }} className="flex-1 h-12 rounded-xl" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
        <Button
          onClick={handleActivate}
          disabled={isLoading}
          className="flex-1 h-12 rounded-xl bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600"
          data-testid="activate-btn"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <CheckCircle className="w-5 h-5 mr-2" />}
          Activate
        </Button>
      </div>
    </div>
  );

  // Success step
  const SuccessUI = () => (
    <div className="space-y-6 text-center" data-testid="success-step">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
        <CheckCircle className="w-10 h-10 text-green-600" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Shipment Activated</h2>
        <p className="text-slate-500 mt-1">Device linked and tracking started</p>
      </div>

      {activationResult?.shipment && (
        <Card>
          <CardContent className="p-4 space-y-3 text-left text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">SSCC</span>
              <code className="font-mono font-semibold bg-slate-100 px-2 py-0.5 rounded">{activationResult.shipment.sscc}</code>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Device</span>
              <span className="font-semibold">{activationResult.device_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Pallet</span>
              <span className="font-mono font-semibold">{activationResult.shipment.pallet_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Status</span>
              <Badge className="bg-green-100 text-green-700">Activated</Badge>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        <Button onClick={() => navigate('/app/dashboard')} className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500" data-testid="go-dashboard-btn">
          View in Dashboard
        </Button>
        <Button onClick={resetWorkflow} variant="outline" className="w-full h-12 rounded-xl" data-testid="scan-another-btn">
          <ScanBarcode className="w-4 h-4 mr-2" /> Scan Another
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-80px)] bg-gradient-to-br from-slate-50 to-blue-50/30 p-6" data-testid="bulk-activation-page">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/app/dashboard')} data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Bulk Activation</h1>
            <p className="text-sm text-slate-500">Scan device + pallet to activate</p>
          </div>
        </div>

        {/* Step indicator */}
        <div className="flex items-center justify-center gap-1 mb-6">
          {STEPS.map((s, i) => (
            <React.Fragment key={s}>
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                  i < currentStep ? 'bg-green-500 text-white' :
                  i === currentStep ? 'bg-blue-600 text-white scale-110' :
                  'bg-slate-200 text-slate-500'
                }`}
                data-testid={`step-${s}`}
              >
                {i < currentStep ? <CheckCircle className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-10 h-0.5 ${i < currentStep ? 'bg-green-500' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step labels */}
        <div className="flex justify-between text-xs text-slate-500 mb-6 px-1">
          <span className={currentStep === 0 ? 'text-blue-600 font-semibold' : ''}>Device</span>
          <span className={currentStep === 1 ? 'text-blue-600 font-semibold' : ''}>Pallet</span>
          <span className={currentStep === 2 ? 'text-blue-600 font-semibold' : ''}>Confirm</span>
          <span className={currentStep === 3 ? 'text-green-600 font-semibold' : ''}>Done</span>
        </div>

        {/* Content card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100">
          {currentStep === 0 && (
            <ScanUI
              label="Step 1: Scan Device"
              hint="Scan the QR code on the IoT tracker"
            />
          )}
          {currentStep === 1 && (
            <ScanUI
              label="Step 2: Scan Pallet"
              hint="Scan the barcode or QR on the pallet"
            />
          )}
          {currentStep === 2 && <ConfirmUI />}
          {currentStep === 3 && <SuccessUI />}
        </div>
      </div>
    </div>
  );
};

export default BulkActivation;
